import axios from "axios";
import { isTokenExpired, clearAuthData } from "./token-utils";

/**
 * Rotas públicas que não requerem autenticação JWT
 */
const publicRoutes = [
  "/auth/send-token",
  "/auth/auth",
  "/auth/send-email-reset-pwd",
  "/user/send-email-reset-pwd",
  "/user/user-registration",
  "/user/set-pwd",
];

/**
 * Verifica se a URL é uma rota pública
 */
function isPublicRoute(url) {
  if (!url) return false;
  
  // Verifica se a URL contém alguma das rotas públicas
  return publicRoutes.some(route => url.includes(route));
}

/**
 * Configura interceptors do axios para:
 * 1. Adicionar token JWT automaticamente em todas as requisições
 * 2. Verificar expiração do token antes de cada requisição (apenas para rotas protegidas)
 * 3. Tratar erros 401 (não autorizado) automaticamente
 */
export function setupAxiosInterceptors(navigate, logout) {
  // Request interceptor - adiciona token e verifica expiração
  axios.interceptors.request.use(
    (config) => {
      const url = config.url || "";
      const isPublic = isPublicRoute(url);
      const token = localStorage.getItem("token");

      // Só verifica expiração do token para rotas protegidas
      if (!isPublic && isTokenExpired()) {
        // Limpa dados de autenticação
        clearAuthData();
        
        // Chama logout se disponível
        if (logout) {
          logout();
        }
        
        // Redireciona para login se navigate estiver disponível
        if (navigate) {
          navigate("/login");
        }
        
        // Cancela a requisição
        return Promise.reject({
          response: {
            status: 401,
            data: { message: "Erro na autenticação" }
          }
        });
      }

      // Adiciona token ao header apenas se existir e não for rota pública
      if (!isPublic && token) {
        config.headers.Authorization = token;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - trata erros 401 e captura novos tokens renovados
  axios.interceptors.response.use(
    (response) => {
      // Verifica se há um novo token renovado nos headers da resposta
      // Headers podem vir em lowercase ou com hífen
      const newToken = response.headers['x-new-token'] || response.headers['X-New-Token'];
      const newTokenExpiration = response.headers['x-token-expiration'] || response.headers['X-Token-Expiration'];

      if (newToken && newTokenExpiration) {
        // Atualiza o token e expiração no localStorage
        localStorage.setItem("token", newToken);
        localStorage.setItem("tokenExpiration", newTokenExpiration);
        
        // Log opcional para debug (remover em produção)
        if (process.env.NODE_ENV === 'development') {
          console.log('Token renovado automaticamente');
        }
      }

      return response;
    },
    (error) => {
      // Verifica se é uma rota pública
      const url = error.config?.url || "";
      const isPublic = isPublicRoute(url);

      // Se receber 401, o token pode estar expirado ou inválido
      // Mas não faz logout/redirect para rotas públicas (como tentativas de login)
      if (error.response && error.response.status === 401 && !isPublic) {
        // Limpa dados de autenticação
        clearAuthData();
        
        // Chama logout se disponível
        if (logout) {
          logout();
        }
        
        // Redireciona para login se navigate estiver disponível
        if (navigate) {
          navigate("/login");
        }
      }

      return Promise.reject(error);
    }
  );
}

